import { supabase } from './supabase'

export const MEDICAL_DOCUMENTS_BUCKET = 'documents-medicaux'
export const MEDICAL_DOCUMENT_SIGNED_URL_TTL_SECONDS = 60 * 60
export const DEFAULT_MEDICAL_DOCUMENT_UPLOAD_CONCURRENCY = 4

type StorageFileLike = Pick<File, 'name' | 'size' | 'lastModified' | 'type'>
type MedicalDocumentLike = {
  file_name?: string | null
  file_url?: string | null
  extracted_data?: Record<string, unknown> | null
}

export interface UploadedMedicalDocumentFile {
  signedUrl: string | null
  storagePath: string
  originalFileName: string
}

function arrayBufferToHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
}

function getNormalizedMedicalDocumentPathSegments(value: string): string[] {
  return value
    .split(/[\\/]+/)
    .map((segment) => segment.trim())
    .filter(Boolean)
}

function getMedicalDocumentExtractedString(
  extractedData: Record<string, unknown> | null | undefined,
  key: string
): string | null {
  const value = extractedData?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

export function getMedicalDocumentSourcePath(
  file: Pick<File, 'name' | 'webkitRelativePath'>
): string {
  return file.webkitRelativePath?.trim() || file.name
}

export function isIgnoredMedicalDocumentSourcePath(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }

  const segments = getNormalizedMedicalDocumentPathSegments(value)

  return segments.some((segment) => {
    const normalizedSegment = segment.trim()

    if (!normalizedSegment) {
      return false
    }

    return (
      normalizedSegment === '__MACOSX' ||
      normalizedSegment === '.DS_Store' ||
      normalizedSegment.startsWith('._') ||
      normalizedSegment.startsWith('.')
    )
  })
}

export function isIgnoredMedicalDocumentFileCandidate(
  file: Pick<File, 'name' | 'webkitRelativePath'>
): boolean {
  return (
    isIgnoredMedicalDocumentSourcePath(getMedicalDocumentSourcePath(file)) ||
    isIgnoredMedicalDocumentSourcePath(file.name)
  )
}

export function isAcceptedMedicalPdfFile(
  file: Pick<File, 'name' | 'type' | 'webkitRelativePath'>
): boolean {
  const lowerName = file.name.toLowerCase()
  const isPdf = file.type === 'application/pdf' || lowerName.endsWith('.pdf')

  return isPdf && !isIgnoredMedicalDocumentFileCandidate(file)
}

export function getMedicalDocumentFileKey(
  file: Pick<File, 'name' | 'size' | 'lastModified' | 'webkitRelativePath'>
): string {
  return `${getMedicalDocumentSourcePath(file)}:${file.size}:${file.lastModified}`
}

export function buildMedicalDocumentDuplicateSignature(
  fileName: string | null | undefined,
  fileSize: number | null | undefined
): string | null {
  if (!fileName || typeof fileSize !== 'number' || !Number.isFinite(fileSize)) {
    return null
  }

  return `${sanitizeMedicalFileName(fileName)}:${fileSize}`
}

export function getStoredMedicalDocumentFingerprint(
  extractedData: Record<string, unknown> | null | undefined
): string | null {
  const value = extractedData?.file_fingerprint_sha256
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
}

export async function computeMedicalDocumentFingerprint(
  file: Pick<File, 'arrayBuffer'>
): Promise<string | null> {
  if (typeof globalThis.crypto?.subtle?.digest !== 'function') {
    return null
  }

  try {
    const buffer = await file.arrayBuffer()
    const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer)
    return arrayBufferToHex(digest)
  } catch (error) {
    console.warn('Impossible de calculer l empreinte du document:', error)
    return null
  }
}

export function sanitizeMedicalFileName(fileName: string): string {
  const trimmedName = fileName.trim()
  const extensionIndex = trimmedName.lastIndexOf('.')
  const baseName = extensionIndex > 0 ? trimmedName.slice(0, extensionIndex) : trimmedName
  const extension = extensionIndex > 0 ? trimmedName.slice(extensionIndex).toLowerCase() : '.pdf'

  const safeBaseName = baseName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80)

  return `${safeBaseName || 'document'}${extension}`
}

export function buildMedicalDocumentStoragePath(
  patientId: string,
  file: Pick<File, 'name'>
): string {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  const randomSuffix =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10)

  return `${patientId}/${timestamp}-${randomSuffix}-${sanitizeMedicalFileName(file.name)}`
}

function normalizeMedicalDocumentStoragePath(value: string): string | null {
  const trimmedValue = value.trim().replace(/^\/+/, '')

  if (!trimmedValue) {
    return null
  }

  if (trimmedValue.startsWith(`${MEDICAL_DOCUMENTS_BUCKET}/`)) {
    return trimmedValue.slice(MEDICAL_DOCUMENTS_BUCKET.length + 1)
  }

  return trimmedValue
}

export function extractMedicalDocumentStoragePath(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }

  if (/^(blob:|data:)/i.test(value)) {
    return null
  }

  if (!/^https?:/i.test(value)) {
    return normalizeMedicalDocumentStoragePath(value)
  }

  try {
    const parsedUrl = new URL(value)
    const bucketPrefixes = [
      `/storage/v1/object/sign/${MEDICAL_DOCUMENTS_BUCKET}/`,
      `/storage/v1/object/public/${MEDICAL_DOCUMENTS_BUCKET}/`,
      `/storage/v1/object/authenticated/${MEDICAL_DOCUMENTS_BUCKET}/`
    ]

    for (const prefix of bucketPrefixes) {
      const prefixIndex = parsedUrl.pathname.indexOf(prefix)

      if (prefixIndex >= 0) {
        const storagePath = parsedUrl.pathname.slice(prefixIndex + prefix.length)
        return normalizeMedicalDocumentStoragePath(decodeURIComponent(storagePath))
      }
    }
  } catch (error) {
    console.warn('Impossible d extraire le chemin storage du document:', error)
  }

  return null
}

export function isMedicalDocumentStoragePath(value: string | null | undefined): boolean {
  return extractMedicalDocumentStoragePath(value) !== null
}

export function isIgnoredMedicalDocumentRecord(document: MedicalDocumentLike): boolean {
  const extractedData =
    document.extracted_data &&
    typeof document.extracted_data === 'object' &&
    !Array.isArray(document.extracted_data)
      ? document.extracted_data
      : null

  return [
    document.file_name,
    getMedicalDocumentExtractedString(extractedData, 'source_relative_path'),
    getMedicalDocumentExtractedString(extractedData, 'original_file_name')
  ].some((value) => isIgnoredMedicalDocumentSourcePath(value))
}

export function filterVisibleMedicalDocuments<T extends MedicalDocumentLike>(documents: T[]): T[] {
  return documents.filter((document) => !isIgnoredMedicalDocumentRecord(document))
}

export async function createSignedMedicalDocumentUrl(
  storagePath: string,
  expiresIn = MEDICAL_DOCUMENT_SIGNED_URL_TTL_SECONDS
): Promise<string> {
  const normalizedStoragePath = extractMedicalDocumentStoragePath(storagePath)

  if (!normalizedStoragePath) {
    throw new Error('Chemin storage medical invalide')
  }

  const { data, error } = await supabase.storage
    .from(MEDICAL_DOCUMENTS_BUCKET)
    .createSignedUrl(normalizedStoragePath, expiresIn)

  if (error || !data?.signedUrl) {
    throw new Error(error?.message || 'Impossible de générer une URL signée pour le document')
  }

  return data.signedUrl
}

export async function resolveMedicalDocumentUrl(fileUrl: string | null | undefined): Promise<string | null> {
  if (!fileUrl) {
    return null
  }

  const storagePath = extractMedicalDocumentStoragePath(fileUrl)

  if (!storagePath) {
    return fileUrl
  }

  try {
    return await createSignedMedicalDocumentUrl(storagePath)
  } catch (error) {
    console.error('Impossible de signer l’URL du document:', error)
    return null
  }
}

export async function resolveMedicalDocumentUrls<T extends { file_url: string | null }>(
  documents: T[]
): Promise<Array<T & { storage_path?: string | null }>> {
  const documentsWithStoragePath = documents.map((document) => ({
    ...document,
    storage_path: extractMedicalDocumentStoragePath(document.file_url)
  }))

  const storagePaths = [
    ...new Set(
      documentsWithStoragePath
        .map((document) => document.storage_path)
        .filter((value): value is string => Boolean(value))
    )
  ]

  const signedUrlMap = new Map<string, string | null>()

  if (storagePaths.length > 0) {
    try {
      const { data, error } = await supabase.storage
        .from(MEDICAL_DOCUMENTS_BUCKET)
        .createSignedUrls(storagePaths, MEDICAL_DOCUMENT_SIGNED_URL_TTL_SECONDS)

      if (error) {
        throw new Error(error.message)
      }

      for (const entry of data || []) {
        if (!entry.path) {
          continue
        }

        signedUrlMap.set(entry.path, entry.error ? null : entry.signedUrl || null)
      }
    } catch (error) {
      console.error('Impossible de signer les URLs des documents en lot:', error)

      await Promise.all(
        storagePaths.map(async (storagePath) => {
          signedUrlMap.set(storagePath, await resolveMedicalDocumentUrl(storagePath))
        })
      )
    }
  }

  return documentsWithStoragePath.map((document) => ({
    ...document,
    file_url: document.storage_path
      ? signedUrlMap.get(document.storage_path) ?? null
      : document.file_url
  }))
}

export async function resolveMedicalDocumentPreviewUrl(document: {
  file_url: string | null
  storage_path?: string | null
}): Promise<string | null> {
  return resolveMedicalDocumentUrl(document.storage_path || document.file_url)
}

export async function uploadMedicalDocumentToPrivateBucket(
  file: StorageFileLike,
  patientId: string
): Promise<UploadedMedicalDocumentFile> {
  const storagePath = buildMedicalDocumentStoragePath(patientId, file)

  const { error } = await supabase.storage
    .from(MEDICAL_DOCUMENTS_BUCKET)
    .upload(storagePath, file as File, {
      contentType: file.type || 'application/pdf',
      upsert: false
    })

  if (error) {
    throw new Error(`Upload failed: ${error.message}`)
  }

  let signedUrl: string | null = null

  try {
    signedUrl = await createSignedMedicalDocumentUrl(storagePath)
  } catch (signError) {
    console.warn('Upload réussi mais URL signée indisponible:', signError)
  }

  return {
    signedUrl,
    storagePath,
    originalFileName: file.name
  }
}

export async function removeMedicalDocumentFromPrivateBucket(storagePath: string | null | undefined): Promise<void> {
  const normalizedStoragePath = extractMedicalDocumentStoragePath(storagePath)

  if (!normalizedStoragePath) {
    return
  }

  const { error } = await supabase.storage
    .from(MEDICAL_DOCUMENTS_BUCKET)
    .remove([normalizedStoragePath])

  if (error) {
    console.warn(`Impossible de supprimer le fichier orphan ${normalizedStoragePath}:`, error.message)
  }
}
